import { InfoCircledIcon } from '@radix-ui/react-icons';
import { Badge, Box, Container, Flex, Text } from '@radix-ui/themes';
import { NotifyHelper } from 'classes/helpers/notify.helper';
import { CommonCallout } from 'components/common/callouts';
import { DialogButton } from 'components/common/dialogs/button';
import { ErrorBoundary } from 'components/common/error-boundary';
import { CommonSelectInput } from 'components/common/form/select';
import { VisionLowerMachineConfirmation } from 'components/machine/dialogs/vision-calibration/lowered-confirmation';
import { RemoteVisionCalibrationStageHeader } from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/stage-header';
import useVisionRemoteStore from 'components/sections/admin-portal/machines/editor/remote-vision-calibration.tab/store/useVisionRemoteStore';
import { useMachinesPortalStore } from 'components/sections/admin-portal/machines/store/use-machines-portal-store';
import { MachineContext } from 'contexts/machine.context';
import { parseISO } from 'date-fns';
import { format } from 'date-fns-tz';
import { IOptionExt } from 'interfaces/forms/option-ext';
import {
  getDistanceOptions,
  MachineHelper,
} from 'lib_ts/classes/machine.helper';
import { feetFloatToString } from 'lib_ts/classes/math.utilities';
import { LOCAL_VERBOSE_DATETIME_FORMAT } from 'lib_ts/enums/datetime.enums';
import { RADIX } from 'lib_ts/enums/radix-ui';
import { IAdminMachine } from 'lib_ts/interfaces/i-machine';
import { isNil } from 'lodash';
import { useCallback, useContext, useMemo, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useAuthStore } from 'stores/use-auth-store';

interface IProps {
  machine: IAdminMachine;
}

export const RemoteVisionCalibrationPlateDistance = ({ machine }: IProps) => {
  const current = useAuthStore((s) => s.current);

  const { logPlateDistanceChange } = useContext(MachineContext);

  const updateMachines = useMachinesPortalStore((s) => s.updateMachines);

  const loading = useVisionRemoteStore((s) => s.loading);

  const [dialogLowerMachine, setDialogLowerMachine] = useState<number>();

  const insetFt = MachineHelper.getWheelsInsetFt(machine.hardware_version);

  // as saved on the machine record
  const currPlateDistanceFt = machine.releaseFt - insetFt;

  // for the controlled input to change w/o affecting the machine object directly
  const [inputFt, setInputFt] = useState(currPlateDistanceFt);

  // the input is expecting a string, and it must be from the allowed options (i.e. rounded to nearest inch)
  const inputValue = useMemo(
    () => MachineHelper.getDistanceOptionValue(inputFt)?.value,
    [inputFt]
  );

  // add a badge to the dropdown option that matches the current value
  const options = useMemo<IOptionExt[]>(() => {
    const currStrValue =
      MachineHelper.getDistanceOptionValue(currPlateDistanceFt)?.value;

    return getDistanceOptions().map((option) => {
      if (option.value !== currStrValue) {
        return option;
      }

      return {
        ...option,
        suffix: <Badge color={RADIX.COLOR.SECONDARY}>Current</Badge>,
      } as IOptionExt;
    });
  }, [currPlateDistanceFt]);

  const onConfirm = useCallback(async () => {
    try {
      const nextReleaseFt = inputFt + insetFt;

      if (nextReleaseFt === machine.releaseFt) {
        // no change, just move on to next stage
        setDialogLowerMachine(Date.now());
        return;
      }

      useVisionRemoteStore.getState().setLoading(true);

      logPlateDistanceChange({
        prevPlateFt: currPlateDistanceFt,
        nextPlateFt: inputFt,
        overrideMachineID: machine.machineID, // * IMPORTANT: spawn for this dialog's machine, not the user's active machine
      });

      await updateMachines([
        {
          _id: machine._id,
          releaseFt: nextReleaseFt, // update releaseFt to the next value
          last_plate_distance_ft: currPlateDistanceFt, // record the current value as the previous value
          last_plate_distance_changed: new Date().toISOString(),
          last_plate_distance_changedBy: current.email,
        },
      ]);

      setDialogLowerMachine(Date.now());
      useVisionRemoteStore.getState().setLoading(false);
    } catch (e) {
      NotifyHelper.error({
        message_md:
          'We were unable to save the plate distance. Please try again or contact support if the issue persists.',
      });
      useVisionRemoteStore.getState().setLoading(false);
    }
  }, [
    inputFt,
    insetFt,
    machine.releaseFt,
    machine._id,
    currPlateDistanceFt,
    current.email,
  ]);

  const plateChangedMsg = useMemo(() => {
    if (isNil(machine.last_plate_distance_ft)) {
      return;
    }

    const lastChangedBy = machine.last_plate_distance_changedBy ?? 'A user';
    const lastChanged = machine.last_plate_distance_changed ?? machine._changed;

    return [
      `${lastChangedBy} changed the plate distance from`,
      `**${feetFloatToString(machine.last_plate_distance_ft)}** to`,
      `**${feetFloatToString(currPlateDistanceFt)}** on`,
      `${format(parseISO(lastChanged), LOCAL_VERBOSE_DATETIME_FORMAT)}`,
    ].join(' ');
  }, [machine, currPlateDistanceFt]);

  return (
    <ErrorBoundary identifier="RemoteVisionCalibrationPlateDistance">
      {/* Radix height prop doesn't apply properly for some reason */}
      <Container size="1" overflow="auto" style={{ height: '100%' }}>
        <Flex direction="column" gap="4">
          <RemoteVisionCalibrationStageHeader />
          {plateChangedMsg && (
            <CommonCallout
              size="1"
              color={RADIX.COLOR.ACCENT}
              icon={
                // todo: update CommonCallout to properly render and align icon and header on the same row
                <Box mt="3px">
                  <InfoCircledIcon />
                </Box>
              }
              content={
                <Flex direction="column">
                  <Text>Previous Plate Distance</Text>

                  <Text color={RADIX.COLOR.SECONDARY}>
                    <ReactMarkdown children={plateChangedMsg} />
                  </Text>
                </Flex>
              }
              flexAlign="start"
            />
          )}
          <CommonSelectInput
            id="machine-plate-distance"
            label="settings.plate-distance"
            options={options}
            value={inputValue}
            onNumericChange={(v) => setInputFt(v)}
            renderDisplayValue={(v) => {
              if (!v) {
                return;
              }

              // so the badge only shows in the dropdown, not in the input once selected
              return MachineHelper.getDistanceOptionValue(Number(v))?.label;
            }}
            skipSort
            suppressWarning
          />
          <DialogButton
            label="Confirm and Proceed to Next Step"
            color={RADIX.COLOR.ACCENT}
            loading={loading}
            disabled={isNil(inputFt) || loading}
            onClick={onConfirm}
            variant="solid"
          />
        </Flex>
        {dialogLowerMachine && (
          <VisionLowerMachineConfirmation
            key={dialogLowerMachine}
            onConfirm={() => useVisionRemoteStore.getState().nextStage()}
            onClose={() => setDialogLowerMachine(undefined)}
          />
        )}
      </Container>
    </ErrorBoundary>
  );
};
